
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { useUserBusiness } from '@/hooks/useUserBusiness';
import { useSubscriptionPlans, useBusinessSubscription, useSubscriptionCheckout, useCustomerPortal } from '@/hooks/useSubscription';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useCommunity } from '@/contexts/CommunityContext';
import { PATHS } from '@/routes/routePaths';
import { useBusinessDiscountForManagement } from '@/hooks/useBusinessDiscount';

// Import components
import SubscriptionHeader from '@/components/subscription/SubscriptionHeader';
import BillingFrequencyTabs from '@/components/subscription/BillingFrequencyTabs';
import PlanCard from '@/components/subscription/PlanCard';
import TrustSection from '@/components/subscription/TrustSection';
import AuthenticationRequired from '@/components/subscription/AuthenticationRequired';
import NoBusinessFound from '@/components/subscription/NoBusinessFound';

const SubscriptionPage = () => {
  const { user } = useAuth();
  const { userBusinessId } = useBusiness();
  const { currentCommunity } = useCommunity();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business_id') || userBusinessId;
  const [selectedInterval, setSelectedInterval] = useState<string>('');
  
  const { data: userBusiness, isLoading: businessLoading } = useUserBusiness();
  const { data: businessDiscount } = useBusinessDiscountForManagement(businessId || userBusiness?.id);
  const { data: plans, isLoading: plansLoading } = useSubscriptionPlans(currentCommunity.id);
  const { data: subscription, isLoading: subscriptionLoading } = 
    useBusinessSubscription(businessId || userBusiness?.id);
  const { createCheckoutSession, loading: checkoutLoading } = useSubscriptionCheckout();
  const { redirectToCustomerPortal, loading: portalLoading } = useCustomerPortal();

  // Check if business is active and redirect if not
  useEffect(() => {
    if (!businessLoading && userBusiness && userBusiness.is_active === false) {
      toast({
        title: "Access Denied",
        description: "Subscription features are only available for active businesses.",
        variant: "destructive"
      });
      navigate(PATHS.ADMIN_MY_BUSINESS);
      return;
    }
    
    // Redirect free businesses
    if (businessDiscount && businessDiscount.type === 'Free') {
      toast({
        title: "Access Denied",
        description: "Subscription features are not available for free businesses.",
        variant: "destructive"
      });
      navigate(PATHS.ADMIN_MY_BUSINESS);
      return;
    }
  }, [userBusiness, businessLoading, businessDiscount, navigate, toast]);

  // Create interval key from interval and interval_count
  const createIntervalKey = (interval: string, intervalCount: number) => {
    return `${interval}-${intervalCount}`;
  };

  // Get all available intervals from active plans only
  const availableIntervals = React.useMemo(() => {
    if (!plans) return [];
    
    const intervalSet = new Set<string>();
    plans.filter(plan => plan.is_active !== false).forEach(plan => {
      plan.available_prices?.forEach(price => {
        const intervalKey = createIntervalKey(price.interval, price.interval_count);
        intervalSet.add(intervalKey);
      });
    });
    
    return Array.from(intervalSet).sort((a, b) => {
      const [intervalA, countA] = a.split('-');
      const [intervalB, countB] = b.split('-');
      const intervalCountA = parseInt(countA);
      const intervalCountB = parseInt(countB);
      
      if (intervalA === 'month' && intervalB === 'year') return -1;
      if (intervalA === 'year' && intervalB === 'month') return 1;
      
      if (intervalA === intervalB) {
        return intervalCountA - intervalCountB;
      }
      
      return 0;
    });
  }, [plans]);

  // Set default interval when intervals are loaded
  useEffect(() => {
    if (availableIntervals.length > 0 && !selectedInterval) {
      setSelectedInterval(availableIntervals[0]);
    }
  }, [availableIntervals, selectedInterval]);

  if (!user) {
    return <AuthenticationRequired />;
  }

  if (businessLoading || plansLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen dashboard-background flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-slate-600 mb-4" />
        <p className="text-lg text-gray-600">Loading subscription information...</p>
      </div>
    );
  }

  if (!userBusiness && !businessId) {
    return <NoBusinessFound />;
  }

  const handleSubscribe = async (priceId: string) => {
    try {
      const targetBusinessId = businessId || userBusiness?.id;
      if (!targetBusinessId) {
        toast({
          title: "Error",
          description: "No business selected for subscription",
          variant: "destructive"
        });
        return;
      }
      await createCheckoutSession(targetBusinessId, priceId);
    } catch (error) {
      console.error("Error subscribing:", error);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const targetBusinessId = businessId || userBusiness?.id;
      if (!targetBusinessId) {
        toast({
          title: "Error",
          description: "No business selected for subscription management",
          variant: "destructive"
        });
        return;
      }
      await redirectToCustomerPortal(targetBusinessId);
    } catch (error) {
      console.error("Error opening customer portal:", error);
    }
  };

  const isSubscribed = subscription?.subscribed;
  const currentPlan = subscription?.current_plan;
  
  // Get active and inactive plans
  const activePlans = plans?.filter(plan => plan.is_active !== false) || [];
  const inactivePlans = plans?.filter(plan => plan.is_active === false) || [];

  return (
    <div className="min-h-screen dashboard-background">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <SubscriptionHeader 
          title="Choose Your Plan"
          subtitle="Select the perfect plan for your business needs. Upgrade your visibility and grow your customer base."
        />

        <BillingFrequencyTabs
          availableIntervals={availableIntervals}
          selectedInterval={selectedInterval}
          onIntervalChange={setSelectedInterval}
        />

        {availableIntervals.length > 0 && (
          <Tabs value={selectedInterval} onValueChange={setSelectedInterval}>
            {availableIntervals.map((intervalKey) => (
              <TabsContent key={intervalKey} value={intervalKey}>
                {/* Flexbox layout for proper centering */}
                <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto">
                  {/* Active Plans */}
                  {activePlans.map((plan) => {
                    const [interval, intervalCountStr] = intervalKey.split('-');
                    const intervalCount = parseInt(intervalCountStr);
                    const currentPrice = plan.available_prices?.find(price => 
                      price.interval === interval && price.interval_count === intervalCount
                    );
                    
                    if (!currentPrice) return null;
                    
                    const isCurrentPlan = currentPlan?.stripe_price_id === currentPrice.id;
                    
                    return (
                      <div key={`${plan.id}-${intervalKey}`} className="w-full max-w-sm">
                        <PlanCard
                          plan={plan}
                          currentPrice={currentPrice}
                          intervalKey={intervalKey}
                          isCurrentPlan={isCurrentPlan}
                          isSubscribed={isSubscribed}
                          checkoutLoading={checkoutLoading}
                          portalLoading={portalLoading}
                          onSubscribe={handleSubscribe}
                          onManageSubscription={handleManageSubscription}
                          communityId={currentCommunity.id}
                        />
                      </div>
                    );
                  })}

                  {/* Inactive Plans */}
                  {inactivePlans.map((plan) => (
                    <div key={`inactive-${plan.id}`} className="w-full max-w-sm">
                      <PlanCard
                        plan={plan}
                        intervalKey={intervalKey}
                        isCurrentPlan={false}
                        isSubscribed={isSubscribed}
                        checkoutLoading={checkoutLoading}
                        portalLoading={portalLoading}
                        onSubscribe={handleSubscribe}
                        onManageSubscription={handleManageSubscription}
                        communityId={currentCommunity.id}
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        <TrustSection />
      </div>
    </div>
  );
};

export default SubscriptionPage;
